import React from 'react';
import './NativeDatePicker.css';

/**
 * NativeDatePicker
 *
 * Drop-in replacement for Carbon's <DatePicker> that uses the browser's
 * native <input type="date"> element. This opens the OS-native date picker
 * on mobile (iOS/Android) and desktop, which is far more reliable than
 * Carbon's custom popup calendar on touch devices.
 *
 * API compatibility with Carbon DatePicker:
 *  - `value` accepts a Date object, a 'YYYY-MM-DD' string, or null/''.
 *  - `onChange` receives an array `[date]` where `date` is a Date object
 *    (or null when cleared), matching Carbon's `dates[0]` convention.
 */
const NativeDatePicker = ({
  id,
  labelText,
  value,
  onChange,
  placeholder,
  disabled = false,
  required = false,
  helperText,
}) => {
  // Convert incoming value (Date | string | null) to 'YYYY-MM-DD' for the input
  const toInputValue = (val) => {
    if (!val) return '';

    if (val instanceof Date) {
      if (isNaN(val.getTime())) return '';
      return `${val.getFullYear()}-${String(val.getMonth() + 1).padStart(2, '0')}-${String(val.getDate()).padStart(2, '0')}`;
    }

    if (typeof val === 'string') {
      // Already in YYYY-MM-DD form
      const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return `${match[1]}-${match[2]}-${match[3]}`;

      // Otherwise try to parse as a date string
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }

    return '';
  };

  const handleChange = (e) => {
    if (!onChange) return;

    const raw = e.target.value; // 'YYYY-MM-DD' or ''
    if (!raw) {
      // Emulate Carbon: dates[0] is null when cleared
      onChange([null]);
      return;
    }

    const [year, month, day] = raw.split('-').map(Number);
    // Emulate Carbon: dates[0] is a Date object (local midnight)
    onChange([new Date(year, month - 1, day)]);
  };

  return (
    <div className="cds--form-item native-date-picker">
      {labelText && (
        <label className="cds--label" htmlFor={id}>
          {labelText}
        </label>
      )}
      <input
        id={id}
        type="date"
        className="cds--text-input native-date-picker__input"
        value={toInputValue(value)}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
      />
      {helperText && (
        <div className="cds--form__helper-text">{helperText}</div>
      )}
    </div>
  );
};

export default NativeDatePicker;
